import {
  EnrichmentField,
  EnrichmentRequest,
  EnrichmentResponse,
  ArticleResult,
  ThumbnailData,
  AppRole,
  LookupType,
  fieldToGraphQL,
  FIELD_TO_PROP,
} from "./types";

const API_URL = process.env.API_URL;

/** Page size for paginated GraphQL queries. */
const PAGE_SIZE = 100;
const IN_FILTER_LIMIT = 100;

function chunkValues<T>(values: T[], chunkSize: number): T[][] {
  const chunks: T[][] = [];

  for (let index = 0; index < values.length; index += chunkSize) {
    chunks.push(values.slice(index, index + chunkSize));
  }

  return chunks;
}

/**
 * Builds the list of GraphQL field names to request, always including ArticleCode.
 * When looking up by Barcode, also includes BaseUnitEanCode for response matching.
 * Thumbnail is skipped (no API field).
 */
function buildFieldSelection(fields: EnrichmentField[], lookupType: LookupType): string {
  const graphqlFields = new Set<string>(["ArticleCode"]);

  if (lookupType === "Barcode") {
    graphqlFields.add("BaseUnitEanCode");
  }

  for (const field of fields) {
    const gqlName = fieldToGraphQL(field);
    if (gqlName) {
      graphqlFields.add(gqlName);
    }
  }

  return Array.from(graphqlFields).join("\n    ");
}

/**
 * Returns the GraphQL query root field name for the given role.
 */
function queryNameForRole(role: AppRole): string {
  return role === AppRole.Sales ? "dimArticleSales" : "dimArticlePurchases";
}

/**
 * Builds the full GraphQL query string for a paginated request.
 */
function buildQuery(request: EnrichmentRequest): string {
  const fieldSelection = buildFieldSelection(request.fields, request.lookupType);
  const queryName = queryNameForRole(request.role);
  const filterField = request.lookupType === "Barcode" ? "BaseUnitEanCode" : "ArticleCode";

  return `
  query GetArticles($codes: [String!]!, $first: Int!, $after: String) {
    ${queryName}(
      first: $first
      after: $after
      filter: { ${filterField}: { in: $codes } }
      orderBy: { ArticleCode: ASC }
    ) {
      items {
        ${fieldSelection}
      }
      hasNextPage
      endCursor
    }
  }`;
}

/**
 * Maps a raw GraphQL response item to an ArticleResult.
 */
function mapResponseItem(
  item: Record<string, unknown>,
  requestedFields: EnrichmentField[]
): ArticleResult {
  const result: ArticleResult = {
    articleCode: item.ArticleCode as string,
  };

  if ("BaseUnitEanCode" in item) {
    result.barcode = item.BaseUnitEanCode as string;
  }

  for (const field of requestedFields) {
    const gqlName = fieldToGraphQL(field);
    const prop = FIELD_TO_PROP[field];
    if (!gqlName || !prop || !(gqlName in item)) continue;

    if (field === EnrichmentField.ThumbnailBinaryAsBase64) {
      const base64 = item[gqlName] as string;
      if (base64) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (result as any)[prop] = {
          base64,
          mimeType: "image/png",
        } satisfies ThumbnailData;
      }
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (result as any)[prop] = item[gqlName];
    }
  }

  return result;
}

/**
 * Fetches enrichment data from the Microsoft Fabric GraphQL API.
 * Handles pagination automatically to retrieve all matching articles.
 *
 * @param request - The enrichment request containing article codes, fields, and role.
 * @param accessToken - Bearer token obtained from MSAL for authenticated access.
 * @param signal - Optional AbortSignal for cancellation support.
 */
export async function fetchEnrichmentData(
  request: EnrichmentRequest,
  accessToken: string,
  signal?: AbortSignal
): Promise<EnrichmentResponse> {
  const query = buildQuery(request);
  const queryName = queryNameForRole(request.role);
  const requestedCodes = Array.from(new Set(request.codes));
  const codeChunks = chunkValues(requestedCodes, IN_FILTER_LIMIT);

  const allItems: ArticleResult[] = [];

  for (const codes of codeChunks) {
    let after: string | null = null;
    let hasNextPage = true;

    while (hasNextPage) {
      const variables: Record<string, unknown> = {
        codes,
        first: PAGE_SIZE,
        after,
      };

      const response = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ query, variables }),
        signal,
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      const json = await response.json();

      if (json.errors && json.errors.length > 0) {
        throw new Error(
          `GraphQL errors: ${json.errors.map((e: { message: string }) => e.message).join(", ")}`
        );
      }

      const connection = json.data?.[queryName];
      if (!connection) {
        throw new Error(`Invalid API response: missing ${queryName} data.`);
      }

      const items = connection.items as Record<string, unknown>[];
      for (const item of items) {
        allItems.push(mapResponseItem(item, request.fields));
      }

      hasNextPage = connection.hasNextPage === true;
      after = connection.endCursor ?? null;
    }
  }

  // Deduplicate: by Barcode when looking up by barcode, otherwise by ArticleCode
  const seen = new Set<string>();
  const dedupedItems = allItems.filter((item) => {
    const key = request.lookupType === "Barcode"
      ? item.barcode ?? item.articleCode
      : item.articleCode;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  // Derive notFound by comparing requested codes against returned values
  const returnedCodes = request.lookupType === "Barcode"
    ? new Set(dedupedItems.map((a) => a.barcode ?? ""))
    : new Set(dedupedItems.map((a) => a.articleCode));
  const notFound = request.codes.filter((code) => !returnedCodes.has(code));

  return {
    articles: dedupedItems,
    notFound,
    errors: [],
  };
}
